import React, { useState, useEffect } from 'react';
import { Search, Edit, Check, AlertCircle, Calendar, MapPin, Users, Heart } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const ModifyRSVPPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';
  
  const [lookupEmail, setLookupEmail] = useState(email);
  const [rsvpData, setRsvpData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const eventId = '9a407f95-8675-4b00-8779-0a9a1d055cd8';
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  const event = {
    title: 'EL Bethel | On Campus - WORSHIP Night',
    date: 'Thursday, December 11, 2025',
    time: '8:00 PM - 10:00 PM',
    venue: 'Rogers Theater, Patty & Bo Pilgrim Chapel',
    address: 'Dallas Baptist University, 3000 Mountain Creek Parkway, Dallas, TX 75211'
  };

  useEffect(() => {
    const urlEmail = searchParams.get('email');
    if (urlEmail) {
      setLookupEmail(urlEmail);
      // Auto-lookup if email is provided in URL
      setTimeout(() => {
        handleLookupWithEmail(urlEmail);
      }, 100);
    }
  }, [searchParams]);

  const handleLookupWithEmail = async (emailToLookup) => {
    if (!emailToLookup.trim()) {
      setError('Please enter your email address');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${API}/events/${eventId}/rsvp/lookup/${encodeURIComponent(emailToLookup)}`);
      
      if (response.ok) {
        const existingRSVP = await response.json();
        setRsvpData(existingRSVP);
        setShowForm(true);
      } else if (response.status === 404) {
        setError('No RSVP found for this email address. Please check your email or register for the event.');
      } else {
        throw new Error('Lookup failed');
      }
    } catch (err) {
      setError('Unable to look up your RSVP. Please try again or contact us for assistance.');
      console.error('RSVP lookup error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLookup = async () => {
    await handleLookupWithEmail(lookupEmail);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setRsvpData({
      ...rsvpData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setIsUpdating(true);
    setError('');

    try {
      const response = await fetch(`${API}/events/${eventId}/rsvp/${rsvpData.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          event_id: eventId,
          name: rsvpData.name,
          email: rsvpData.email,
          phone: rsvpData.phone,
          guests_count: rsvpData.guests_count,
          dietary_restrictions: rsvpData.dietary_restrictions,
          special_requests: rsvpData.special_requests,
          rsvp_status: rsvpData.rsvp_status,
          needs_transportation: rsvpData.needs_transportation,
          pickup_location: rsvpData.pickup_location,
          transportation_guests_count: rsvpData.transportation_guests_count,
          can_provide_rides: rsvpData.can_provide_rides,
          vehicle_capacity: rsvpData.vehicle_capacity,
          transportation_notes: rsvpData.transportation_notes
        }),
      });

      if (!response.ok) {
        throw new Error('Update failed');
      }

      setSuccess(true);
    } catch (err) {
      setError('Unable to update your RSVP. Please try again or contact us for assistance.');
      console.error('RSVP update error:', err);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="pt-16 lg:pt-20 pb-20 lg:pb-0 min-h-screen">
      {/* Hero Section */}
      <section className="py-12 lg:py-20 bg-gradient-to-br from-orange-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 lg:mb-12">
            <Edit className="w-16 h-16 text-[#f17b21] mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-800 mb-6 font-display">
              Modify Your RSVP
            </h1>
            <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto font-sans">
              Need to update your registration for the December 11th worship night? 
              We're here to help you make any necessary changes.
            </p>
          </div>

          {/* Event Info Banner */}
          <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-lg p-6 mb-8">
            <div className="flex items-center space-x-4 mb-4">
              <Calendar className="w-8 h-8 text-[#f17b21]" />
              <div>
                <h3 className="text-xl font-bold text-gray-800 font-display">{event.title}</h3>
                <p className="text-gray-600 font-sans">{event.date} • {event.time}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <MapPin className="w-5 h-5 text-[#f17b21] mt-1" />
              <div>
                <p className="font-semibold text-gray-800 font-sans">{event.venue}</p>
                <p className="text-sm text-gray-600 font-sans">{event.address}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RSVP Lookup/Modification Section */}
      <section className="py-12 lg:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {!showForm ? (
              /* Email Lookup Form */
              <div className="bg-gray-50 rounded-2xl p-8 lg:p-12 text-center">
                <Search className="w-12 h-12 text-[#f17b21] mx-auto mb-6" />
                <h2 className="text-2xl lg:text-3xl font-bold text-gray-800 mb-6 font-display">
                  Find Your RSVP
                </h2>
                <p className="text-lg text-gray-600 mb-8 font-sans">
                  Enter the email address you used to register for the December 11th event.
                </p>

                <form onSubmit={(e) => { e.preventDefault(); handleLookup(); }} className="max-w-md mx-auto">
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-2 font-sans">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={lookupEmail}
                      onChange={(e) => setLookupEmail(e.target.value)}
                      placeholder="your.email@example.com"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f17b21] focus:border-transparent font-sans"
                      required
                    />
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                      <div className="flex items-center space-x-2">
                        <AlertCircle className="w-5 h-5 text-red-600" />
                        <p className="text-red-600 text-sm font-sans">{error}</p>
                      </div>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-[#f17b21] hover:bg-[#d86919] text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-300 font-sans flex items-center justify-center space-x-2 disabled:opacity-70"
                  >
                    {isLoading ? (
                      <>
                        <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                        <span>Looking up your RSVP...</span>
                      </>
                    ) : (
                      <>
                        <Search className="w-5 h-5" />
                        <span>Find My RSVP</span>
                      </>
                    )}
                  </button>
                </form>

                <div className="mt-8 pt-8 border-t border-gray-200">
                  <p className="text-gray-600 font-sans mb-4">
                    Don't have an RSVP yet?
                  </p>
                  <button
                    onClick={() => navigate('/DBU')}
                    className="border-2 border-[#f17b21] text-[#f17b21] hover:bg-[#f17b21] hover:text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-300 font-sans"
                  >
                    Register for Event
                  </button>
                </div>
              </div>
            ) : success ? (
              /* Success Message */
              <div className="bg-green-50 rounded-2xl p-8 lg:p-12 text-center">
                <Check className="w-16 h-16 text-green-600 mx-auto mb-6" />
                <h2 className="text-3xl lg:text-4xl font-bold text-gray-800 mb-6 font-display">
                  RSVP Updated Successfully!
                </h2>
                <p className="text-xl text-gray-600 mb-8 font-sans">
                  Your registration for the December 11th worship night has been updated.
                </p>

                <div className="bg-white rounded-xl p-6 mb-8 shadow-lg">
                  <h3 className="text-xl font-bold text-gray-800 mb-4 font-display">Updated Registration</h3>
                  <div className="space-y-3 text-left max-w-md mx-auto">
                    <div className="flex justify-between">
                      <span className="font-sans">Name:</span>
                      <span className="font-semibold font-sans">{rsvpData.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-sans">Guests:</span>
                      <span className="font-semibold font-sans">{rsvpData.guests_count}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-sans">Status:</span>
                      <span className={`font-semibold font-sans ${
                        rsvpData.rsvp_status === 'attending' ? 'text-green-600' :
                        rsvpData.rsvp_status === 'maybe' ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {rsvpData.rsvp_status.charAt(0).toUpperCase() + rsvpData.rsvp_status.slice(1)}
                      </span>
                    </div>
                    {rsvpData.needs_transportation && (
                      <div className="flex justify-between">
                        <span className="font-sans">Transportation:</span>
                        <span className="font-semibold font-sans text-blue-600">Needs ride</span>
                      </div>
                    )}
                    {rsvpData.can_provide_rides && (
                      <div className="flex justify-between">
                        <span className="font-sans">Providing rides:</span>
                        <span className="font-semibold font-sans text-green-600">{rsvpData.vehicle_capacity} seats</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  <button
                    onClick={() => navigate('/DBU')}
                    className="bg-[#f17b21] hover:bg-[#d86919] text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-300 font-sans mr-4"
                  >
                    Back to Event Page
                  </button>
                  <button
                    onClick={() => {
                      setShowForm(false);
                      setSuccess(false);
                      setRsvpData(null);
                      setLookupEmail('');
                    }}
                    className="border-2 border-[#f17b21] text-[#f17b21] hover:bg-[#f17b21] hover:text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-300 font-sans"
                  >
                    Modify Another RSVP
                  </button>
                </div>
              </div>
            ) : (
              /* Modification Form */
              <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-12">
                <div className="text-center mb-8">
                  <h2 className="text-2xl lg:text-3xl font-bold text-gray-800 mb-4 font-display">
                    Update Your Registration
                  </h2>
                  <p className="text-lg text-gray-600 font-sans">
                    Make any necessary changes to your RSVP for the December 11th event.
                  </p>
                </div>

                <form onSubmit={handleUpdate} className="space-y-6">
                  {/* Basic Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2 font-sans">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={rsvpData?.name || ''}
                        onChange={handleInputChange}
                        placeholder="Enter your full name"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f17b21] focus:border-transparent font-sans"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2 font-sans">
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={rsvpData?.email || ''}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 font-sans"
                        disabled
                      />
                      <p className="text-xs text-gray-500 mt-1 font-sans">Email cannot be changed</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2 font-sans">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={rsvpData?.phone || ''}
                        onChange={handleInputChange}
                        placeholder="(123) 456-7890"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f17b21] focus:border-transparent font-sans"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2 font-sans">
                        Number of Guests (Including You)
                      </label>
                      <select
                        name="guests_count"
                        value={rsvpData?.guests_count || '1'}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f17b21] focus:border-transparent font-sans"
                      >
                        <option value="1">1 Person</option>
                        <option value="2">2 People</option>
                        <option value="3">3 People</option>
                        <option value="4">4 People</option>
                        <option value="5">5 People</option>
                        <option value="6+">6+ People</option>
                      </select>
                    </div>
                  </div>

                  {/* RSVP Status */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-4 font-sans">
                      RSVP Status
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="radio"
                          name="rsvp_status"
                          value="attending"
                          checked={rsvpData?.rsvp_status === 'attending'}
                          onChange={handleInputChange}
                          className="w-4 h-4 text-[#f17b21] border-gray-300 focus:ring-[#f17b21]"
                        />
                        <span className="font-semibold text-green-600 font-sans">Attending</span>
                      </label>
                      <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="radio"
                          name="rsvp_status"
                          value="maybe"
                          checked={rsvpData?.rsvp_status === 'maybe'}
                          onChange={handleInputChange}
                          className="w-4 h-4 text-[#f17b21] border-gray-300 focus:ring-[#f17b21]"
                        />
                        <span className="font-semibold text-yellow-600 font-sans">Maybe</span>
                      </label>
                      <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="radio"
                          name="rsvp_status"
                          value="declined"
                          checked={rsvpData?.rsvp_status === 'declined'}
                          onChange={handleInputChange}
                          className="w-4 h-4 text-[#f17b21] border-gray-300 focus:ring-[#f17b21]"
                        />
                        <span className="font-semibold text-red-600 font-sans">Cannot Attend</span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 font-sans">
                      Dietary Restrictions (for dinner)
                    </label>
                    <input
                      type="text"
                      name="dietary_restrictions"
                      value={rsvpData?.dietary_restrictions || ''}
                      onChange={handleInputChange}
                      placeholder="Any allergies or dietary needs?"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f17b21] focus:border-transparent font-sans"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 font-sans">
                      Special Requests or Questions
                    </label>
                    <textarea
                      name="special_requests"
                      value={rsvpData?.special_requests || ''}
                      onChange={handleInputChange}
                      placeholder="Any special requests or questions about the event?"
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f17b21] focus:border-transparent resize-none font-sans"
                    />
                  </div>

                  {/* Transportation Section - Same as RSVP forms */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <h4 className="text-lg font-bold text-gray-800 mb-4 font-display">Transportation Coordination</h4>
                    <p className="text-gray-600 text-sm mb-6 font-sans">
                      Update your transportation needs or availability to help others.
                    </p>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="flex items-start space-x-3 cursor-pointer">
                          <input
                            type="checkbox"
                            name="needs_transportation"
                            checked={rsvpData?.needs_transportation || false}
                            onChange={handleInputChange}
                            className="w-5 h-5 text-[#f17b21] border-gray-300 rounded focus:ring-[#f17b21] mt-1"
                          />
                          <div>
                            <p className="font-semibold text-gray-800 font-sans">I need a ride to the event</p>
                            <p className="text-sm text-gray-600 font-sans">Check this if you need transportation assistance</p>
                          </div>
                        </label>
                      </div>

                      {rsvpData?.needs_transportation && (
                        <div className="ml-8 space-y-4 pl-4 border-l-2 border-[#f17b21]">
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2 font-sans">
                              Pickup Location *
                            </label>
                            <input
                              type="text"
                              name="pickup_location"
                              value={rsvpData?.pickup_location || ''}
                              onChange={handleInputChange}
                              placeholder="Where should we pick you up?"
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f17b21] focus:border-transparent font-sans"
                              required
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2 font-sans">
                              Transportation Guest Count
                            </label>
                            <select
                              name="transportation_guests_count"
                              value={rsvpData?.transportation_guests_count || ''}
                              onChange={handleInputChange}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f17b21] focus:border-transparent font-sans"
                            >
                              <option value="">Same as RSVP guest count</option>
                              <option value="1">1 Person</option>
                              <option value="2">2 People</option>
                              <option value="3">3 People</option>
                              <option value="4">4 People</option>
                              <option value="5">5 People</option>
                              <option value="6+">6+ People</option>
                            </select>
                          </div>
                        </div>
                      )}

                      <div>
                        <label className="flex items-start space-x-3 cursor-pointer">
                          <input
                            type="checkbox"
                            name="can_provide_rides"
                            checked={rsvpData?.can_provide_rides || false}
                            onChange={handleInputChange}
                            className="w-5 h-5 text-[#f17b21] border-gray-300 rounded focus:ring-[#f17b21] mt-1"
                          />
                          <div>
                            <p className="font-semibold text-gray-800 font-sans">I can provide rides to others</p>
                            <p className="text-sm text-gray-600 font-sans">Check this if you can help transport others</p>
                          </div>
                        </label>
                      </div>

                      {rsvpData?.can_provide_rides && (
                        <div className="ml-8 space-y-4 pl-4 border-l-2 border-green-500">
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2 font-sans">
                              Available Seats (not including yourself)
                            </label>
                            <select
                              name="vehicle_capacity"
                              value={rsvpData?.vehicle_capacity || ''}
                              onChange={handleInputChange}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f17b21] focus:border-transparent font-sans"
                            >
                              <option value="">Select available seats</option>
                              <option value="1">1 Extra Seat</option>
                              <option value="2">2 Extra Seats</option>
                              <option value="3">3 Extra Seats</option>
                              <option value="4">4 Extra Seats</option>
                              <option value="5">5 Extra Seats</option>
                              <option value="6+">6+ Extra Seats</option>
                            </select>
                          </div>
                        </div>
                      )}

                      {(rsvpData?.needs_transportation || rsvpData?.can_provide_rides) && (
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2 font-sans">
                            Transportation Notes
                          </label>
                          <textarea
                            name="transportation_notes"
                            value={rsvpData?.transportation_notes || ''}
                            onChange={handleInputChange}
                            placeholder="Any additional transportation details..."
                            rows={2}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f17b21] focus:border-transparent resize-none font-sans"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-center space-x-2">
                        <AlertCircle className="w-5 h-5 text-red-600" />
                        <p className="text-red-600 text-sm font-sans">{error}</p>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row gap-4">
                    <button
                      type="submit"
                      disabled={isUpdating}
                      className="flex-1 bg-gradient-to-r from-[#f17b21] to-[#ff9649] hover:from-[#d86919] hover:to-[#e8853a] text-white px-8 py-3 rounded-lg font-bold transition-all duration-300 transform hover:scale-105 disabled:opacity-70 disabled:cursor-not-allowed font-sans flex items-center justify-center space-x-2"
                    >
                      {isUpdating ? (
                        <>
                          <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                          <span>Updating RSVP...</span>
                        </>
                      ) : (
                        <>
                          <Check className="w-5 h-5" />
                          <span>Update My RSVP</span>
                        </>
                      )}
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => setShowForm(false)}
                      className="border-2 border-gray-300 text-gray-700 hover:bg-gray-50 px-8 py-3 rounded-lg font-semibold transition-colors duration-300 font-sans"
                    >
                      Cancel
                    </button>
                  </div>
                </form>

                <div className="mt-8 pt-8 border-t border-gray-200 text-center">
                  <p className="text-gray-600 text-sm font-sans">
                    Need help? Contact us at{' '}
                    <a href="mailto:contact@elbethel.online" className="text-[#f17b21] hover:text-[#d86919] font-semibold">
                      contact@elbethel.online
                    </a>
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ModifyRSVPPage;